import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { UserProfileService } from '../services/userProfileService';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  business_name?: string;
  phone?: string;
  avatar_url?: string;
  bio?: string;
  website?: string;
  location?: string;
  is_admin?: boolean;
  is_active?: boolean;
  subscription_plan?: 'free' | 'premium' | 'enterprise';
  subscription_expires_at?: string;
  total_campaigns?: number;
  total_qr_codes?: number;
  total_scans?: number;
  notification_preferences?: {
    email: boolean;
    push: boolean;
    reports: boolean;
  };
  created_at: string;
  updated_at: string;
}

export interface UserMetadata {
  language: string;
  darkMode: boolean;
  theme: string;
  notifications: boolean;
}

interface UserProfileContextType {
  profile: UserProfile | null;
  metadata: UserMetadata | null;
  isLoading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<boolean>;
  updateMetadata: (updates: Partial<UserMetadata>) => Promise<boolean>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const useUserProfileContext = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfileContext must be used within a UserProfileProvider');
  }
  return context;
};

interface UserProfileProviderProps {
  children: ReactNode;
}

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({ children }) => {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [metadata, setMetadata] = useState<UserMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    if (!authUser?.id) {
      setProfile(null);
      setMetadata(null);
      return;
    }

    // DISABLED TO PREVENT DATABASE SPAM - USE STATIC FALLBACK PROFILE
    // Set static fallback profile (non-admin by default)
    const staticProfile: UserProfile = {
      id: authUser.id,
      name: authUser.user_metadata?.name || authUser.email?.split('@')[0] || 'User',
      email: authUser.email || 'user@example.com',
      business_name: undefined,
      phone: undefined,
      avatar_url: undefined,
      bio: undefined,
      website: undefined,
      location: undefined,
      is_admin: false, // Default to non-admin for static fallback
      is_active: true,
      subscription_plan: 'free',
      subscription_expires_at: undefined,
      total_campaigns: 0,
      total_qr_codes: 0,
      total_scans: 0,
      notification_preferences: {
        email: true,
        push: true,
        reports: true
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    setProfile(staticProfile);
    setMetadata({
      language: 'English',
      darkMode: false,
      theme: 'light',
      notifications: true
    });
    setIsLoading(false);
  };

  const refreshProfile = async () => {
    await fetchProfile();
  };

  const updateProfile = async (updates: Partial<UserProfile>): Promise<boolean> => {
    if (!authUser?.id) return false;

    try {
      // DISABLED - just update local state without network request
      setProfile(prev => prev ? { ...prev, ...updates } : null);
      return true;
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      return false;
    }
  };

  const updateMetadata = async (updates: Partial<UserMetadata>): Promise<boolean> => {
    if (!authUser?.id) return false;

    try {
      // DISABLED - just update local state without network request
      setMetadata(prev => prev ? { ...prev, ...updates } : null);

      // Apply dark mode immediately
      if (updates.darkMode !== undefined) {
        if (updates.darkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('darkMode', updates.darkMode.toString());
      }
      
      // Apply language immediately
      if (updates.language) {
        localStorage.setItem('language', updates.language);
      }
      
      return true;
    } catch (err) {
      console.error('Error updating metadata:', err);
      setError(err instanceof Error ? err.message : 'Failed to update metadata');
      return false;
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [authUser]);

  // Apply dark mode on initial load
  useEffect(() => {
    if (metadata?.darkMode !== undefined) {
      if (metadata.darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, [metadata?.darkMode]);

  const value = useMemo<UserProfileContextType>(() => ({
    profile,
    metadata,
    isLoading,
    error,
    refreshProfile,
    updateProfile,
    updateMetadata,
  }), [profile, metadata, isLoading, error, refreshProfile, updateProfile, updateMetadata]);

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export default UserProfileContext;
